import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Plus, Pencil, Trash2, Wallet, Check, X, Search,
  Calendar, AlertCircle, Eye
} from "lucide-react";
import toast from "react-hot-toast";

import PilotageField from "@/components/pilotage/PilotageField";
import PilotageModal from "@/components/pilotage/PilotageModal";
import AppEmptyState from "@/components/ui/AppEmptyState";
import AppErrorState from "@/components/ui/AppErrorState";
import AppLoadingState from "@/components/ui/AppLoadingState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getApiErrorMessage } from "@/lib/api";
import { 
  EXPENSE_CATEGORIES, 
  EXPENSE_STATUSES,
  listCollaborators,
  listMissions,
  createExpense,
  updateExpense,
  deleteExpense,
  approveExpense,
  rejectExpense,
  getExpensesByCollaborator
} from "@/lib/pilotageApi";
import { formatEUR0 } from "@/lib/pilotageUi";

const safeArray = (value) => Array.isArray(value) ? value : [];
const safeNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

const YEARS = [2024, 2025, 2026, 2027, 2028];
const MONTHS = [
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

const EMPTY_FORM = {
  collaborator_id: "",
  collaborator_name: "",
  mission_id: "",
  label: "",
  category: "OTHER",
  amount: "",
  status: "SUBMITTED",
  type: "ONE_TIME", // ONE_TIME, RANGE, ONGOING
  start_month: "", // YYYY-MM
  end_month: "",   // YYYY-MM
  notes: "",
  is_billable_to_client: false
};

const getCategoryLabelFrench = (cat) => {
  const mapping = {
    TRANSPORT: "Transport",
    MEAL: "Repas",
    OTHER: "Autre",
    DEPLACEMENT: "Déplacement",
    HOTEL: "Hôtel",
    REPAS: "Repas",
    MATERIEL: "Matériel",
    LICENCE: "Licence",
    ONBOARDING: "Onboarding",
    FORMATION: "Formation",
    PRIME: "Prime",
    TELEPHONE: "Téléphone",
    EQUIPEMENT: "Équipement",
    PORTAGE: "Portage",
    ADMINISTRATIF: "Administratif"
  };
  return mapping[cat] || cat;
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "APPROVED":
      return "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20";
    case "SUBMITTED":
      return "bg-amber-500/10 text-amber-400 border border-amber-500/20";
    case "REJECTED":
      return "bg-rose-500/10 text-rose-400 border border-rose-500/20";
    default:
      return "bg-slate-500/10 text-slate-400 border border-slate-500/20";
  }
};

const getStatusTextFrench = (status) => {
  switch (status) {
    case "APPROVED":
      return "Approuvé";
    case "SUBMITTED":
      return "En attente";
    case "REJECTED":
      return "Rejeté";
    default:
      return status;
  }
};

const formatPeriodLabel = (exp) => {
  if (exp.mode === "ONE_TIME") {
    return exp.start_date || exp.date || "-";
  }
  const start = exp.start_date ? exp.start_date.substring(0, 7) : "";
  const end = exp.end_date ? exp.end_date.substring(0, 7) : "";
  if (start && end) {
    return `De ${start} à ${end}`;
  }
  return `À partir de ${start} (permanent)`;
};

const getExpenseId = (expense) => expense?.id || expense?._id || expense?.expense_id;

const normalizeMonthInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) return value;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 7);
  return "";
};

const getMonthBounds = (yyyyMm) => {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return null;
  const [year, month] = yyyyMm.split("-").map(Number);
  const lastDayDate = new Date(year, month, 0);
  const lastDayNum = lastDayDate.getDate();
  const pad = (n) => String(n).padStart(2, "0");
  return {
    firstDay: `${year}-${pad(month)}-01`,
    lastDay: `${year}-${pad(month)}-${pad(lastDayNum)}`
  };
};

const isMissionActiveForExpenseMonth = (mission, expenseMonth) => {
  if (!mission) return false;
  if (!expenseMonth) return true;
  
  const bounds = getMonthBounds(expenseMonth);
  if (!bounds) return true;
  
  const startStr = String(mission.start_date || "").slice(0, 10);
  const endStr = mission.end_date ? String(mission.end_date).slice(0, 10) : "";
  
  const isStarted = !startStr || startStr <= bounds.lastDay;
  const isNotEnded = !endStr || endStr >= bounds.firstDay;
  
  const isStatusEligible = !mission.status || ["ACTIVE", "PLANNED", "ENDED"].includes(String(mission.status || "").toUpperCase());
  
  return isStarted && isNotEnded && isStatusEligible;
};

const resolveCollaboratorLabel = (expense, collaborators = []) => {
  if (!expense?.collaborator_id) return "";
  const id = String(expense.collaborator_id);
  const found = (collaborators || []).find((c) => String(c.id || c._id) === id);
  return expense?.collaborator_name || found?.full_name || found?.name || "";
};

const resolveMissionLabel = (expense, missions = []) => {
  if (!expense?.mission_id) return "";
  const id = String(expense.mission_id);
  const found = (missions || []).find((m) => String(m.id || m._id) === id);
  return expense?.mission_name || found?.client_name || found?.title || found?.name || found?.label || "";
};

const mapExpenseToFormState = (expense, collaboratorsList = [], missionsList = []) => {
  let type = "ONE_TIME";
  const mode = expense?.mode || expense?.period_type || "ONE_TIME";
  if (mode === "MONTHLY") {
    type = expense.end_date ? "RANGE" : "ONGOING";
  }
  
  return {
    collaborator_id: expense?.collaborator_id || "",
    collaborator_name: resolveCollaboratorLabel(expense, collaboratorsList),
    mission_id: expense?.mission_id || "",
    mission_name: resolveMissionLabel(expense, missionsList),
    label: expense?.label || expense?.comment || expense?.title || expense?.description || "",
    category: expense?.category || "OTHER",
    amount: String(expense?.amount ?? ""),
    status: expense?.status || "SUBMITTED",
    type: type,
    start_month: normalizeMonthInput(expense?.start_date || expense?.start_month || expense?.date),
    end_month: normalizeMonthInput(expense?.end_date || expense?.end_month),
    is_billable_to_client: Boolean(expense?.is_billable_to_client),
    notes: expense?.notes || "",
  };
};

const FraisCollaborateursPage = () => {
  // Page state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(""); // empty = "Tous"
  const [selectedCollaboratorFilter, setSelectedCollaboratorFilter] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
  const [searchText, setSearchText] = useState("");

  // Lists for dropdowns
  const [collaboratorsList, setCollaboratorsList] = useState([]);
  const [missionsList, setMissionsList] = useState([]);

  // Detail Drawer / Panel
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState({ collaborator: null, month: null, expenses: [] });

  // CRUD Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Fetch reference lists (once)
  useEffect(() => {
    const fetchReferenceLists = async () => {
      try {
        const [collabs, missions] = await Promise.all([
          listCollaborators(),
          listMissions()
        ]);
        setCollaboratorsList(collabs);
        setMissionsList(missions);
      } catch (err) {
        console.error("Erreur chargement références", err);
      }
    };
    fetchReferenceLists();
  }, []);

  // Build collaborator options
  const collaboratorOptions = useMemo(() => {
    const normalizeId = (value) => String(value || "").trim();
    const normalizeText = (value) => String(value || "").trim().toLowerCase();
    const getCollaboratorKey = (collab) => {
      if (!collab) return "";
      return normalizeId(collab.id || collab._id || collab.collaborator_id) ||
        normalizeText(collab.email) ||
        normalizeText(collab.full_name || collab.name);
    };

    const getDisplayName = (collab) => {
      if (!collab) return "";
      if (collab.full_name) return String(collab.full_name).trim();
      if (collab.first_name || collab.last_name) {
        return `${collab.first_name || ""} ${collab.last_name || ""}`.trim();
      }
      if (collab.name) return String(collab.name).trim();
      if (collab.email) return String(collab.email).trim();
      if (collab.collaborator_name) return String(collab.collaborator_name).trim();
      return normalizeId(collab.id || collab._id || collab.collaborator_id);
    };

    const items = [];

    // 1. Collaborators from listCollaborators API
    for (const c of safeArray(collaboratorsList)) {
      if (c) {
        items.push({
          _id: normalizeId(c._id || c.id),
          email: c.email || "",
          full_name: getDisplayName(c),
          status: c.status
        });
      }
    }

    // 2. Collaborators from listMissions API
    for (const m of safeArray(missionsList)) {
      if (m && m.collaborator_id) {
        items.push({
          _id: normalizeId(m.collaborator_id),
          email: m.collaborator_email || "",
          full_name: m.collaborator_name || "",
          status: m.status
        });
      }
    }

    // 3. Collaborators from current expenses
    const expensesCollabs = safeArray(data?.collaborators);
    for (const ec of expensesCollabs) {
      if (ec) {
        const ecId = normalizeId(ec.collaborator_id || ec.id || ec._id);
        if (ecId && ecId !== "unassigned" && ecId !== "null" && ecId !== "undefined") {
          items.push({
            _id: ecId,
            email: ec.collaborator_email || "",
            full_name: ec.collaborator_name || "",
          });
        }
      }
    }

    // 4. Fallback for the currently selected collaborator
    if (formValues.collaborator_id) {
      items.push({
        _id: normalizeId(formValues.collaborator_id),
        full_name: formValues.collaborator_name || formValues.collaborator_id
      });
    }

    // Deduplicate
    const map = new Map();
    for (const item of items) {
      const key = getCollaboratorKey(item);
      if (!key) continue;
      
      const existing = map.get(key);
      if (!existing) {
        map.set(key, item);
      } else {
        // Merge to keep most info
        const merged = { ...existing, ...item };
        merged._id = existing._id || item._id;
        merged.full_name = (existing.full_name && !/^[0-9a-fA-F]{24}$/.test(existing.full_name))
          ? existing.full_name
          : (item.full_name && !/^[0-9a-fA-F]{24}$/.test(item.full_name)) ? item.full_name : existing.full_name;
        merged.email = existing.email || item.email;
        map.set(key, merged);
      }
    }

    return safeArray(Array.from(map.values()))
      .filter(c => c._id && c._id !== "unassigned" && c._id !== "null" && c._id !== "undefined")
      .map(c => {
        // Final sanity check on name to make sure we don't display ObjectId
        if (!c.full_name || /^[0-9a-fA-F]{24}$/.test(c.full_name)) {
          c.full_name = c.email || c._id;
        }
        return c;
      });
  }, [collaboratorsList, missionsList, data, formValues.collaborator_id, formValues.collaborator_name]);

  // Build mission options
  const missionOptions = useMemo(() => {
    let filtered = [...safeArray(missionsList)];

    if (formValues.collaborator_id) {
      filtered = filtered.filter(m => m && String(m.collaborator_id || "") === String(formValues.collaborator_id));
    }

    // Filter by month/période
    const startMonth = formValues.start_month;
    const endMonth = formValues.end_month;
    
    if (startMonth) {
      if (formValues.type === "RANGE" && endMonth) {
        filtered = filtered.filter(m => {
          if (!m) return false;
          const startBounds = getMonthBounds(startMonth);
          const endBounds = getMonthBounds(endMonth);
          if (!startBounds || !endBounds) return true;
          
          const startStr = String(m.start_date || "").slice(0, 10);
          const endStr = m.end_date ? String(m.end_date).slice(0, 10) : "";
          
          const isStarted = !startStr || startStr <= endBounds.lastDay;
          const isNotEnded = !endStr || endStr >= startBounds.firstDay;
          const isStatusEligible = !m.status || ["ACTIVE", "PLANNED", "ENDED"].includes(String(m.status || "").toUpperCase());
          return isStarted && isNotEnded && isStatusEligible;
        });
      } else {
        filtered = filtered.filter(m => m && isMissionActiveForExpenseMonth(m, startMonth));
      }
    }

    // Keep currently selected mission even if it doesn't match the collaborator filter or month filter or isn't in missionsList
    if (formValues.mission_id && !filtered.some(m => m && String(m._id || m.id) === String(formValues.mission_id))) {
      const selectedMission = safeArray(missionsList).find(m => m && String(m._id || m.id) === String(formValues.mission_id));
      if (selectedMission) {
        filtered.push(selectedMission);
      } else {
        // Fallback option if not in reference lists at all
        filtered.push({
          _id: formValues.mission_id,
          id: formValues.mission_id,
          client_name: formValues.mission_name || formValues.mission_id,
          collaborator_name: ""
        });
      }
    }

    return filtered;
  }, [missionsList, formValues.collaborator_id, formValues.mission_id, formValues.mission_name, formValues.start_month, formValues.end_month, formValues.type]);

  // Automatically reset mission_id if the selected mission is no longer eligible after a month or collaborator change
  useEffect(() => {
    if (formValues.mission_id && formValues.mission_id !== "none") {
      const currentMission = safeArray(missionsList).find(m => m && String(m._id || m.id) === String(formValues.mission_id));
      if (currentMission) {
        let isEligible = true;
        
        // 1. Check collaborator match
        if (formValues.collaborator_id && String(currentMission.collaborator_id || "") !== String(formValues.collaborator_id)) {
          isEligible = false;
        }
        
        // 2. Check month match
        if (isEligible && formValues.start_month) {
          if (formValues.type === "RANGE" && formValues.end_month) {
            const startBounds = getMonthBounds(formValues.start_month);
            const endBounds = getMonthBounds(formValues.end_month);
            if (startBounds && endBounds) {
              const startStr = String(currentMission.start_date || "").slice(0, 10);
              const endStr = currentMission.end_date ? String(currentMission.end_date).slice(0, 10) : "";
              const isStarted = startStr <= endBounds.lastDay;
              const isNotEnded = !endStr || endStr >= startBounds.firstDay;
              const isStatusEligible = ["ACTIVE", "PLANNED", "ENDED"].includes(String(currentMission.status || "").toUpperCase());
              isEligible = isStarted && isNotEnded && isStatusEligible;
            }
          } else {
            isEligible = isMissionActiveForExpenseMonth(currentMission, formValues.start_month);
          }
        }
        
        if (!isEligible) {
          setFormValues(prev => ({ ...prev, mission_id: "", mission_name: "" }));
        }
      }
    }
  }, [formValues.collaborator_id, formValues.start_month, formValues.end_month, formValues.type, formValues.mission_id, missionsList]);


  // Fetch main aggregated data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        year: selectedYear,
      };
      if (selectedMonth) params.month = selectedMonth;
      if (selectedCollaboratorFilter) params.collaborator_id = selectedCollaboratorFilter;
      if (selectedCategoryFilter) params.category = selectedCategoryFilter;
      if (selectedStatusFilter) params.status = selectedStatusFilter;

      const response = await getExpensesByCollaborator(params);
      setData(response);

      // If drawer is open, update its contents
      if (drawerOpen && response) {
        const currentCollabId = drawerData.collaborator?.collaborator_id;
        const currentMonthNum = drawerData.month;
        const collaborators = safeArray(response.collaborators);
        const updatedCollab = collaborators.find(c => c.collaborator_id === currentCollabId);
        
        if (updatedCollab) {
          let updatedExpenses = [];
          const monthsList = safeArray(updatedCollab.months);
          if (currentMonthNum) {
            const monthDetail = monthsList.find(m => m.month === currentMonthNum);
            updatedExpenses = monthDetail ? safeArray(monthDetail.expenses) : [];
          } else {
            // All months
            updatedExpenses = monthsList.flatMap(m => safeArray(m?.expenses));
          }
          setDrawerData({
            collaborator: updatedCollab,
            month: currentMonthNum,
            expenses: updatedExpenses
          });
        } else {
          setDrawerOpen(false);
        }
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Impossible de charger les frais collaborateurs."));
    } finally {
      setLoading(false);
    }
  }, [
    selectedYear,
    selectedMonth,
    selectedCollaboratorFilter,
    selectedCategoryFilter,
    selectedStatusFilter,
    drawerOpen,
    drawerData.collaborator?.collaborator_id,
    drawerData.month
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Text Search (Client-side search helper)
  const filteredCollaborators = useMemo(() => {
    const collabs = safeArray(data?.collaborators);
    if (collabs.length === 0) return [];
    if (!searchText.trim()) return collabs;

    const term = searchText.toLowerCase();
    return collabs.map(c => {
      if (!c) return null;
      // Check if collaborator name matches
      const nameMatch = (c.collaborator_name || "").toLowerCase().includes(term);
      // Filter months where expenses match comment or label or category or notes
      const months = safeArray(c.months);
      const filteredMonths = months.map(m => {
        if (!m) return { expenses: [], total: 0, approved_total: 0, pending_total: 0 };
        const expenses = safeArray(m.expenses);
        const matchingExpenses = expenses.filter(e => 
          (e?.comment && e.comment.toLowerCase().includes(term)) ||
          (e?.label && e.label.toLowerCase().includes(term)) ||
          (e?.notes && e.notes.toLowerCase().includes(term)) ||
          (e?.category && e.category.toLowerCase().includes(term))
        );
        return {
          ...m,
          expenses: matchingExpenses,
          total: matchingExpenses.reduce((sum, exp) => exp?.status !== "REJECTED" ? sum + (exp?.amount || 0) : sum, 0),
          approved_total: matchingExpenses.reduce((sum, exp) => exp?.status === "APPROVED" ? sum + (exp?.amount || 0) : sum, 0),
          pending_total: matchingExpenses.reduce((sum, exp) => exp?.status === "SUBMITTED" ? sum + (exp?.amount || 0) : sum, 0),
        };
      });

      const hasMatchingExpenses = filteredMonths.some(m => m.expenses.length > 0);

      if (nameMatch || hasMatchingExpenses) {
        // Recalculate totals based on text filters
        const approved = filteredMonths.reduce((sum, m) => sum + (m.approved_total || 0), 0);
        const pending = filteredMonths.reduce((sum, m) => sum + (m.pending_total || 0), 0);
        return {
          ...c,
          months: filteredMonths,
          annual_total: approved + pending,
          approved_total: approved,
          pending_total: pending
        };
      }
      return null;
    }).filter(Boolean);
  }, [data, searchText]);

  // Open drawer for a collaborator + optional month
  const handleOpenDrawer = (collab, monthNum = null) => {
    const months = safeArray(collab?.months);
    let expenses;
    if (monthNum) {
      const monthDetail = months.find(m => m.month === monthNum);
      expenses = safeArray(monthDetail?.expenses);
    } else {
      // All months
      expenses = months.flatMap(m => safeArray(m?.expenses));
    }
    
    // Deduplicate expenses (could be mapped to multiple months if recurring)
    const uniqueExpenses = Array.from(new Map(expenses.map(item => [getExpenseId(item), item])).values());

    setDrawerData({
      collaborator: collab,
      month: monthNum,
      expenses: uniqueExpenses
    });
    setDrawerOpen(true);
  };

  // CRUD Actions
  const closeModal = () => {
    setModalOpen(false);
    setEditingExpense(null);
    setFormValues(EMPTY_FORM);
    setFormError("");
  };

  const handleOpenCreateModal = () => {
    setEditingExpense(null);
    setFormValues({
      ...EMPTY_FORM,
      start_month: `${selectedYear}-01`,
      end_month: `${selectedYear}-12`,
    });
    setFormError("");
    setModalOpen(true);
  };

  const handleOpenEditModal = (expense) => {
    setEditingExpense(expense);
    setFormValues(mapExpenseToFormState(expense, collaboratorsList, missionsList));
    setFormError("");
    setModalOpen(true);
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce frais ?")) {
      return;
    }
    const toastId = toast.loading("Suppression du frais en cours...");
    try {
      await deleteExpense(expenseId);
      toast.success("Frais supprimé avec succès.", { id: toastId });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erreur lors de la suppression."), { id: toastId });
    }
  };

  const handleApproveExpense = async (expenseId) => {
    const toastId = toast.loading("Validation en cours...");
    try {
      await approveExpense(expenseId);
      toast.success("Frais approuvé avec succès.", { id: toastId });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erreur de validation."), { id: toastId });
    }
  };

  const handleRejectExpense = async (expenseId) => {
    const toastId = toast.loading("Rejet en cours...");
    try {
      await rejectExpense(expenseId);
      toast.success("Frais rejeté avec succès.", { id: toastId });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erreur lors du rejet."), { id: toastId });
    }
  };

  const handleSaveExpense = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setSaving(true);
    setFormError("");

    // Validation
    const amountNum = Number(formValues.amount);
    if (!amountNum || amountNum <= 0) {
      setFormError("Le montant doit être supérieur à 0.");
      setSaving(false);
      return;
    }
    if (!formValues.start_month) {
      setFormError("Le mois de début est obligatoire.");
      setSaving(false);
      return;
    }
    if (formValues.type === "RANGE" && !formValues.end_month) {
      setFormError("Le mois de fin est obligatoire pour une plage mensuelle.");
      setSaving(false);
      return;
    }

    const resolvedCollabId = !formValues.collaborator_id || formValues.collaborator_id === "unassigned" ? null : formValues.collaborator_id;
    let resolvedCollabName = "";
    if (resolvedCollabId) {
      const collab = safeArray(collaboratorOptions).find(c => c._id === resolvedCollabId);
      resolvedCollabName = collab ? collab.full_name : (formValues.collaborator_name || "");
    }

    const resolvedMissionId = !formValues.mission_id || formValues.mission_id === "none" ? null : formValues.mission_id;
    let resolvedMissionName = "";
    if (resolvedMissionId) {
      const mission = safeArray(missionsList).find(m => m._id === resolvedMissionId);
      resolvedMissionName = mission ? `${mission.client_name} - ${mission.collaborator_name}` : (formValues.mission_name || "");
    }

    // Build payload
    const payload = {
      collaborator_id: resolvedCollabId,
      collaborator_name: resolvedCollabId ? resolvedCollabName : null,
      mission_id: resolvedMissionId,
      mission_name: resolvedMissionId ? resolvedMissionName : null,
      label: formValues.label || null,
      comment: formValues.label || null, // duplicate label into comment for backward-compatibility
      category: formValues.category,
      amount: amountNum,
      status: formValues.status,
      mode: formValues.type === "ONE_TIME" ? "ONE_TIME" : "MONTHLY",
      start_date: `${formValues.start_month}-01`,
      end_date: formValues.type === "ONE_TIME" 
        ? null 
        : formValues.type === "ONGOING" 
          ? null 
          : `${formValues.end_month}-01`, // first day of end month
      notes: formValues.notes || null,
      is_billable_to_client: formValues.is_billable_to_client
    };

    // date field is required by legacy schemas in some places
    payload.date = payload.start_date;

    const toastId = toast.loading("Enregistrement du frais...");
    try {
      if (editingExpense) {
        await updateExpense(getExpenseId(editingExpense), payload);
        toast.success("Frais modifié avec succès.", { id: toastId });
      } else {
        await createExpense(payload);
        toast.success("Frais créé avec succès.", { id: toastId });
      }
      closeModal();
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Impossible de sauvegarder le frais."), { id: toastId });
      setFormError(getApiErrorMessage(err, "Erreur de validation."));
    } finally {
      setSaving(false);
    }
  };

  // Derived totals
  const totals = useMemo(() => {
    const t = data?.totals;
    return {
      annual_total: safeNumber(t?.annual_total),
      approved_total: safeNumber(t?.approved_total),
      pending_total: safeNumber(t?.pending_total),
      rejected_total: safeNumber(t?.rejected_total),
    };
  }, [data]);
  const monthlyAverage = useMemo(() => {
    if (!safeArray(data?.collaborators).length) return 0;
    // Compute total by month across all collaborators
    const count = selectedMonth ? 1 : 12;
    return safeNumber(totals.annual_total) / count;
  }, [data, totals, selectedMonth]);

  return (
    <div className="space-y-6">
      {/* HEADER SECTION */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-white/10 pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
            <Wallet className="h-6 w-6 text-lenvia-blue" />
            Frais collaborateurs
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Frais approuvés et en attente déduits de la marge et du résultat net. Ils ne diminuent pas le CA.
          </p>
        </div>
        <Button 
          type="button" 
          onClick={handleOpenCreateModal}
          className="bg-lenvia-blue text-white hover:bg-lenvia-blue/90"
        >
          <Plus className="mr-2 h-4 w-4" />
          Ajouter un frais
        </Button>
      </div>

      {/* FILTERS & SEARCH */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-white/10 bg-slate-900/40 p-4 rounded-xl backdrop-blur-sm">
        <PilotageField label="Année">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="w-full h-10 rounded-lg border border-white/10 bg-slate-900/60 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
          >
            {safeArray(YEARS).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </PilotageField>

        <PilotageField label="Mois">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full h-10 rounded-lg border border-white/10 bg-slate-900/60 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
          >
            <option value="">Tous les mois</option>
            {safeArray(MONTHS).map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </PilotageField>

        <PilotageField label="Collaborateur">
          <select
            value={selectedCollaboratorFilter}
            onChange={(e) => setSelectedCollaboratorFilter(e.target.value)}
            className="w-full h-10 rounded-lg border border-white/10 bg-slate-900/60 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
          >
            <option value="">Tous</option>
            <option value="unassigned">Non affecté</option>
            {safeArray(collaboratorsList).map(c => (
              <option key={c._id} value={c._id}>
                {c.full_name || `${c.first_name} ${c.last_name}`}
              </option>
            ))}
          </select>
        </PilotageField>

        <PilotageField label="Catégorie">
          <select
            value={selectedCategoryFilter}
            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            className="w-full h-10 rounded-lg border border-white/10 bg-slate-900/60 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
          >
            <option value="">Toutes</option>
            {safeArray(EXPENSE_CATEGORIES).map(cat => (
              <option key={cat} value={cat}>{getCategoryLabelFrench(cat)}</option>
            ))}
          </select>
        </PilotageField>

        <PilotageField label="Statut">
          <select
            value={selectedStatusFilter}
            onChange={(e) => setSelectedStatusFilter(e.target.value)}
            className="w-full h-10 rounded-lg border border-white/10 bg-slate-900/60 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
          >
            <option value="">Tous</option>
            {safeArray(EXPENSE_STATUSES).map(st => (
              <option key={st} value={st}>{getStatusTextFrench(st)}</option>
            ))}
          </select>
        </PilotageField>

        <PilotageField label="Recherche">
          <div className="relative">
            <Search className="absolute left-2.5 top-3 h-4 w-4 text-slate-500" />
            <Input
              type="text"
              placeholder="Rechercher..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 w-full h-10 border-white/10 bg-slate-900/60 text-white"
            />
          </div>
        </PilotageField>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="border border-white/10 bg-slate-900/50 p-4 rounded-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Frais annuels totaux</p>
          <h2 className="text-2xl font-bold text-white mt-1">{formatEUR0(totals.annual_total)}</h2>
          <p className="text-[10px] text-slate-400 mt-1">Approuvés + En attente</p>
        </div>

        <div className="border border-emerald-500/10 bg-emerald-500/[0.02] p-4 rounded-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-500">Frais approuvés</p>
          <h2 className="text-2xl font-bold text-emerald-400 mt-1">{formatEUR0(totals.approved_total)}</h2>
          <p className="text-[10px] text-slate-400 mt-1">Comptés dans le P&L</p>
        </div>

        <div className="border border-amber-500/10 bg-amber-500/[0.02] p-4 rounded-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-500">Frais en attente</p>
          <h2 className="text-2xl font-bold text-amber-400 mt-1">{formatEUR0(totals.pending_total)}</h2>
          <p className="text-[10px] text-slate-400 mt-1">Comptés dans le P&L</p>
        </div>

        <div className="border border-rose-500/10 bg-rose-500/[0.02] p-4 rounded-xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-rose-500">Frais rejetés</p>
          <h2 className="text-2xl font-bold text-rose-400 mt-1">{formatEUR0(totals.rejected_total)}</h2>
          <p className="text-[10px] text-slate-400 mt-1">Exclus du P&L</p>
        </div>

        <div className="border border-white/10 bg-slate-900/50 p-4 rounded-xl col-span-2 md:col-span-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Moyenne mensuelle</p>
          <h2 className="text-2xl font-bold text-white mt-1">{formatEUR0(monthlyAverage)}</h2>
          <p className="text-[10px] text-slate-400 mt-1">Sur la base sélectionnée</p>
        </div>
      </div>

      {/* DATA STATES */}
      {loading && !data ? (
        <AppLoadingState message="Chargement des frais collaborateurs..." />
      ) : error ? (
        <AppErrorState message={error} onRetry={fetchData} />
      ) : filteredCollaborators.length === 0 ? (
        <AppEmptyState title="Aucun frais trouvé" description="Ajustez vos filtres ou ajoutez un nouveau frais pour commencer." />
      ) : (
        /* TABLE */
        <div className="overflow-x-auto border border-white/10 bg-slate-900/20 rounded-xl">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="border-b border-white/10 bg-slate-900/60 text-xs font-semibold uppercase text-slate-400">
              <tr>
                <th className="px-4 py-3">Collaborateur</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right text-emerald-400">Approuvé</th>
                <th className="px-4 py-3 text-right text-amber-400">En attente</th>
                <th className="px-4 py-3 text-right text-rose-400">Rejeté</th>
                {safeArray(MONTHS).map(m => (
                  <th key={m.value} className="px-2 py-3 text-center w-12">{m.label.substring(0, 3)}.</th>
                ))}
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-slate-300">
              {safeArray(filteredCollaborators).map(c => (
                <tr 
                  key={c.collaborator_id || "unassigned"}
                  className="hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">
                    {c.collaborator_name}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-white whitespace-nowrap">
                    {formatEUR0(c.annual_total)}
                  </td>
                  <td className="px-4 py-3 text-right text-emerald-400 whitespace-nowrap">
                    {formatEUR0(c.approved_total)}
                  </td>
                  <td className="px-4 py-3 text-right text-amber-400 whitespace-nowrap">
                    {formatEUR0(c.pending_total)}
                  </td>
                  <td className="px-4 py-3 text-right text-rose-400 whitespace-nowrap">
                    {formatEUR0(c.rejected_total || 0)}
                  </td>
                  
                  {/* Months cells */}
                  {safeArray(MONTHS).map(m => {
                    const cMonths = safeArray(c?.months);
                    const monthDetail = cMonths.find(mon => mon.month === m.value);
                    const totalVal = monthDetail ? monthDetail.total : 0;
                    return (
                      <td 
                        key={m.value}
                        onClick={() => handleOpenDrawer(c, m.value)}
                        className={`px-2 py-3 text-center cursor-pointer transition-colors hover:bg-lenvia-blue/20 hover:text-white ${
                          totalVal > 0 ? "font-semibold text-white bg-lenvia-blue/5" : "text-slate-600"
                        }`}
                      >
                        {totalVal > 0 ? formatEUR0(totalVal) : "-"}
                      </td>
                    );
                  })}

                  <td className="px-4 py-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDrawer(c)}
                      className="text-slate-400 hover:text-white"
                      title="Voir le détail annuel"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DETAIL DRAWER */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="relative w-full max-w-2xl bg-slate-900 border-l border-white/10 h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            {/* Drawer Header */}
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-slate-900/80 sticky top-0">
              <div>
                <h3 className="text-lg font-bold text-white">
                  Détail des frais — {drawerData.collaborator?.collaborator_name}
                </h3>
                <p className="text-sm text-slate-400 mt-0.5">
                  {drawerData.month ? `Pour le mois de ${MONTHS.find(m => m.value === drawerData.month)?.label} ${selectedYear}` : `Tous les frais de l'année ${selectedYear}`}
                </p>
              </div>
              <button 
                type="button" 
                onClick={() => setDrawerOpen(false)}
                className="text-slate-400 hover:text-white rounded-lg p-1.5 hover:bg-white/5"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {safeArray(drawerData.expenses).length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <AlertCircle className="h-10 w-10 mx-auto text-slate-600 mb-3" />
                  <p>Aucun frais enregistré pour cette période.</p>
                </div>
              ) : (
                safeArray(drawerData.expenses).map((exp) => (
                  <div 
                    key={exp.id} 
                    className="border border-white/10 bg-slate-950/40 p-4 rounded-xl space-y-3 relative group"
                  >
                    {/* Upper row: category badge and status badge */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold bg-slate-800 text-slate-300 px-2.5 py-1 rounded-full border border-white/5">
                        {getCategoryLabelFrench(exp.category)}
                      </span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${getStatusBadgeClass(exp.status)}`}>
                        {getStatusTextFrench(exp.status)}
                      </span>
                    </div>

                    {/* Comment/Label and Amount */}
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="font-semibold text-white text-base">
                          {exp.label || exp.comment || "Sans libellé"}
                        </h4>
                        {exp.comment && exp.label && (
                          <p className="text-sm text-slate-400 mt-1 italic">"{exp.comment}"</p>
                        )}
                        {exp.notes && (
                          <p className="text-xs text-slate-500 mt-2 bg-slate-900/60 p-2 rounded-lg border border-white/5">
                            {exp.notes}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-white">{formatEUR0(exp.amount)}</span>
                      </div>
                    </div>

                    {/* Metadata & Details */}
                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-400 border-t border-white/5 pt-3">
                      <div>
                        <span className="text-slate-500 block">Période / Date</span>
                        <span className="flex items-center gap-1 mt-0.5">
                          <Calendar className="h-3 w-3 text-slate-500" />
                          {formatPeriodLabel(exp)}
                        </span>
                      </div>
                      {exp.mission_id && (
                        <div>
                          <span className="text-slate-500 block">Mission</span>
                          <span className="truncate block mt-0.5" title={exp.mission_id}>
                            {safeArray(missionsList).find(m => m._id === exp.mission_id)?.client_name || exp.mission_id}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-slate-500 block">Refacturable client</span>
                        <span className="mt-0.5 block">{exp.is_billable_to_client ? "Oui" : "Non"}</span>
                      </div>
                    </div>

                    {/* Hover Actions */}
                    <div className="flex justify-end gap-2 border-t border-white/5 pt-3">
                      {exp.status === "SUBMITTED" && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleApproveExpense(exp.id)}
                            className="h-8 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            title="Approuver le frais"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approuver
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRejectExpense(exp.id)}
                            className="h-8 px-2 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                            title="Rejeter le frais"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Rejeter
                          </Button>
                        </>
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenEditModal(exp)}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                        title="Modifier"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteExpense(exp.id)}
                        className="h-8 w-8 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* CRUD MODAL */}
      <PilotageModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={editingExpense ? "Modifier le frais" : "Ajouter un nouveau frais"}
        onSubmit={handleSaveExpense}
        isSubmitting={saving}
      >
        <div className="space-y-4">
          {formError && (
            <div className="border border-rose-500/20 bg-rose-500/10 p-3 rounded-lg flex items-center gap-2 text-rose-400 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <p>{formError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PilotageField label="Collaborateur concerné">
              <select
                value={formValues.collaborator_id || ""}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const foundCollab = safeArray(collaboratorOptions).find(c => String(c._id) === String(selectedId));
                  setFormValues(prev => ({
                    ...prev,
                    collaborator_id: selectedId,
                    collaborator_name: foundCollab ? foundCollab.full_name : "",
                    // Reset mission if not applicable to new collaborator
                    mission_id: prev.mission_id && safeArray(missionsList).some(m => String(m._id || m.id) === String(prev.mission_id) && String(m.collaborator_id) === String(selectedId))
                      ? prev.mission_id
                      : ""
                  }));
                }}
                className="w-full h-10 rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                <option value="">Non affecté</option>
                {safeArray(collaboratorOptions).map(c => (
                  <option key={c._id} value={c._id}>
                    {c.full_name}
                  </option>
                ))}
              </select>
            </PilotageField>

            <PilotageField label="Mission liée (optionnel)">
              <select
                value={formValues.mission_id || ""}
                onChange={(e) => {
                  const mId = e.target.value;
                  const mission = safeArray(missionsList).find(m => String(m._id || m.id) === String(mId));
                  setFormValues(prev => {
                    const next = { ...prev, mission_id: mId };
                    if (mission) {
                      next.mission_name = mission.client_name ? `${mission.client_name} - ${mission.collaborator_name}` : (mission.title || mission.name || "");
                      if (mission.collaborator_id) {
                        next.collaborator_id = mission.collaborator_id;
                        const collab = safeArray(collaboratorOptions).find(c => String(c._id) === String(mission.collaborator_id));
                        next.collaborator_name = collab ? collab.full_name : (mission.collaborator_name || "");
                      }
                    } else {
                      next.mission_name = "";
                    }
                    return next;
                  });
                }}
                className="w-full h-10 rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                <option value="">Aucune mission — intercontrat</option>
                {safeArray(missionOptions).map(m => {
                  if (!m) return null;
                  const startStr = m.start_date ? String(m.start_date).slice(0, 7) : "";
                  const endStr = m.end_date ? String(m.end_date).slice(0, 7) : "";
                  const period = startStr ? ` (${startStr}${endStr ? ' à ' + endStr : '...' })` : '';
                  const client = m.client_name || "Mission";
                  const collab = m.collaborator_name || m.collaborator_full_name ? ` - ${m.collaborator_name || m.collaborator_full_name}` : '';
                  const label = `${client}${collab}${period}`;
                  return (
                    <option key={m._id || m.id} value={m._id || m.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </PilotageField>
          </div>

          <PilotageField label="Libellé / Titre du frais">
            <Input
              type="text"
              required
              value={formValues.label}
              onChange={(e) => setFormValues({ ...formValues, label: e.target.value })}
              placeholder="Ex: Abonnement Slack, Achat ordinateur portable..."
              className="border-white/10 bg-slate-900 text-white"
            />
          </PilotageField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PilotageField label="Catégorie">
              <select
                value={formValues.category}
                onChange={(e) => setFormValues({ ...formValues, category: e.target.value })}
                className="w-full h-10 rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                {safeArray(EXPENSE_CATEGORIES).map(cat => (
                  <option key={cat} value={cat}>{getCategoryLabelFrench(cat)}</option>
                ))}
              </select>
            </PilotageField>

            <PilotageField label="Montant (€)">
              <Input
                type="number"
                required
                min="0.01"
                step="0.01"
                value={formValues.amount}
                onChange={(e) => setFormValues({ ...formValues, amount: e.target.value })}
                placeholder="0.00"
                className="border-white/10 bg-slate-900 text-white"
              />
            </PilotageField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PilotageField label="Statut de validation">
              <select
                value={formValues.status}
                onChange={(e) => setFormValues({ ...formValues, status: e.target.value })}
                className="w-full h-10 rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                {safeArray(EXPENSE_STATUSES).map(st => (
                  <option key={st} value={st}>{getStatusTextFrench(st)}</option>
                ))}
              </select>
            </PilotageField>

            <PilotageField label="Type de frais">
              <select
                value={formValues.type}
                onChange={(e) => setFormValues({ ...formValues, type: e.target.value })}
                className="w-full h-10 rounded-lg border border-white/10 bg-slate-900 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                <option value="ONE_TIME">Ponctuel (unique)</option>
                <option value="RANGE">Plage mensuelle (début / fin)</option>
                <option value="ONGOING">Permanent mensuel (sans fin)</option>
              </select>
            </PilotageField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PilotageField label="Mois de début / unique">
              <Input
                type="month"
                required
                value={formValues.start_month}
                onChange={(e) => setFormValues({ ...formValues, start_month: e.target.value })}
                className="border-white/10 bg-slate-900 text-white"
              />
            </PilotageField>

            {formValues.type === "RANGE" && (
              <PilotageField label="Mois de fin (inclus)">
                <Input
                  type="month"
                  required
                  value={formValues.end_month}
                  onChange={(e) => setFormValues({ ...formValues, end_month: e.target.value })}
                  className="border-white/10 bg-slate-900 text-white"
                />
              </PilotageField>
            )}
          </div>

          <div className="flex items-center gap-2 py-2">
            <input
              type="checkbox"
              id="is_billable_to_client"
              checked={formValues.is_billable_to_client}
              onChange={(e) => setFormValues({ ...formValues, is_billable_to_client: e.target.checked })}
              className="rounded border-white/10 bg-slate-900 text-lenvia-blue focus:ring-lenvia-blue h-4 w-4"
            />
            <label htmlFor="is_billable_to_client" className="text-sm text-slate-300 select-none cursor-pointer">
              Refacturable au client
            </label>
          </div>

          <PilotageField label="Commentaires / Notes additionnelles">
            <Textarea
              value={formValues.notes}
              onChange={(e) => setFormValues({ ...formValues, notes: e.target.value })}
              placeholder="Notes, justifications, références de facture..."
              className="border-white/10 bg-slate-900 text-white min-h-[80px]"
            />
          </PilotageField>
        </div>
      </PilotageModal>
    </div>
  );
};

export default FraisCollaborateursPage;
